import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/projects/IPO/components/ui/table";
import { PivotFibonacciRow } from "@/utils/types/Screener/Screener";

interface TechnicalsTabProps {
  company: any;
  latestPivot: PivotFibonacciRow | null;
}

const TechnicalsTab = ({ company, latestPivot }: TechnicalsTabProps) => {
  return (
    <div className="max-w-9xl mx-auto space-y-12 duration-500 animate-in fade-in">
      {/* Technical Indicators */}
      <div className="space-y-6">
        <h3 className="text-lg font-bold text-gray-900">
          Technical Indicators
        </h3>
        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
          {[
            {
              label: "RSI (14)",
              value: company?.technicalIndicators?.rsi,
              signal: company?.technicalIndicators?.rsi
                ? company.technicalIndicators.rsi < 30
                  ? "Oversold"
                  : company.technicalIndicators.rsi > 70
                    ? "Overbought"
                    : "Neutral"
                : "—",
            },
            {
              label: "MACD",
              value: company?.technicalIndicators?.macd,
              signal: company?.technicalIndicators?.signal,
            },
            {
              label: "ADX (14)",
              value: company?.technicalIndicators?.adx,
              signal: company?.technicalIndicators?.adx
                ? company.technicalIndicators.adx > 25
                  ? "Strong Trend"
                  : "Weak Trend"
                : "—",
            },
            {
              label: "Williams %R",
              value: company?.technicalIndicators?.williamsR,
              signal: "—",
            },
            {
              label: "CCI (14)",
              value: company?.technicalIndicators?.cci,
              signal: "—",
            },
            {
              label: "Stoch %K",
              value: company?.technicalIndicators?.stochasticK,
              signal: "—",
            },
            {
              label: "Stoch %D",
              value: company?.technicalIndicators?.stochasticD,
              signal: "—",
            },
            {
              label: "Trend",
              value: company?.technicalIndicators?.trend,
              signal: "Summary",
            },
          ].map(indicator => (
            <div
              key={indicator.label}
              className="space-y-2 rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
            >
              <p className="text-xs font-medium text-gray-500">
                {indicator.label}
              </p>
              <div className="flex items-baseline justify-between">
                <span className="text-lg font-bold text-gray-900">
                  {typeof indicator.value === "number"
                    ? indicator.value.toFixed(2)
                    : (indicator.value ?? "—")}
                </span>
              </div>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                  indicator.signal === "Bullish" ||
                  indicator.signal === "Oversold" ||
                  indicator.signal === "Strong Trend"
                    ? "bg-green-100 text-green-600"
                    : indicator.signal === "Bearish" ||
                        indicator.signal === "Overbought"
                      ? "bg-red-100 text-red-600"
                      : "bg-gray-100 text-gray-500"
                }`}
              >
                {indicator.signal ?? "Neutral"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Moving Averages */}
      <div className="space-y-6">
        <h3 className="text-lg font-bold text-gray-900">Moving Averages</h3>
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <Table>
            <TableHeader className="bg-gray-50/50">
              <TableRow>
                <TableHead className="py-4 pl-6 text-xs font-bold text-gray-500">
                  Period
                </TableHead>
                <TableHead className="py-4 text-right text-xs font-bold text-gray-900">
                  SMA
                </TableHead>
                <TableHead className="py-4 pr-6 text-right text-xs font-bold text-gray-900">
                  EMA
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {["10", "20", "50", "100", "200"].map(period => (
                <TableRow
                  key={period}
                  className="border-gray-50 transition-colors hover:bg-gray-50/50"
                >
                  <TableCell className="py-4 pl-6 text-sm font-bold text-gray-700">
                    {period} Day
                  </TableCell>
                  <TableCell className="py-4 text-right text-sm font-medium text-gray-900">
                    {company?.movingAverages?.sma?.[period]
                      ? company.movingAverages.sma[period].toFixed(2)
                      : "—"}
                  </TableCell>
                  <TableCell className="py-4 pr-6 text-right text-sm font-medium text-gray-900">
                    {company?.movingAverages?.ema?.[period]
                      ? company.movingAverages.ema[period].toFixed(2)
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pivot Points */}
      <div className="space-y-6">
        <h3 className="text-lg font-bold text-gray-900">
          Pivot Points (Classic)
        </h3>
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col items-center space-y-4">
            <div className="grid w-full grid-cols-7 text-center text-xs font-medium text-gray-500">
              <span>S3</span>
              <span>S2</span>
              <span>S1</span>
              <span className="font-bold text-[#00D09C]">Pivot</span>
              <span>R1</span>
              <span>R2</span>
              <span>R3</span>
            </div>
            <div className="relative h-2 w-full rounded-full bg-gray-100">
              <div className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00D09C] shadow-sm ring-4 ring-white" />
            </div>
            <div className="grid w-full grid-cols-7 text-center text-sm font-bold text-gray-900">
              <span>{latestPivot?.S3 ? latestPivot.S3.toFixed(4) : "—"}</span>
              <span>{latestPivot?.S2 ? latestPivot.S2.toFixed(4) : "—"}</span>
              <span>{latestPivot?.S1 ? latestPivot.S1.toFixed(4) : "—"}</span>
              <span className="text-[#00D09C]">
                {latestPivot?.PivotPoint
                  ? latestPivot.PivotPoint.toFixed(4)
                  : "—"}
              </span>
              <span>{latestPivot?.R1 ? latestPivot.R1.toFixed(4) : "—"}</span>
              <span>{latestPivot?.R2 ? latestPivot.R2.toFixed(4) : "—"}</span>
              <span>{latestPivot?.R3 ? latestPivot.R3.toFixed(4) : "—"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default TechnicalsTab;
